// THE APPS IN OTHER LANGUAGES (founder 2026-10-14): Polski, Română, Português
// (European), Español, the website's four. English is the SOURCE and the default.
// Plain JS, no dependencies: the Business app, the worker app, shared/ helpers and
// node tests all read it. Each app binds it to React in its own lib/i18n.js
// (useT, useLanguage, useFormat), so screens never import this file directly.
//
// ── Where the words live ─────────────────────────────────────────────────────
// One folder per source file, mirroring its path, so two people converting two
// screens never touch the same file (no central index to edit):
//
//   practice/i18n/app/theme/en.js       THE SOURCE: `export default { ... }`, nested
//                                       objects and arrays of strings. Copy Studio
//                                       reads these (scripts/copy-catalogue).
//   practice/i18n/app/theme/pl.json     { "messages": { same shape }, "stamps": { ... } }
//   practice/i18n/app/theme/ro.json     (pt.json, es.json the same)
//   practice/i18n/app/theme/index.js    defineMessages('practice/app/theme', { en, pl, ro, pt, es })
//
// `node scripts/app-i18n-check.mjs scaffold practice/app/theme.jsx` writes the
// folder. shared/ files use shared/i18n/messages/<file>/ the same way.
//
// ── Stale tracking (as on the website) ──────────────────────────────────────
// A translation is used only while its stamp says it was made from the CURRENT
// English: stamps[key] = "<hash of the English>.<hash of the translation>",
// written by `app-i18n-check.mjs stamp`. When the English changes, that one
// string shows in English until it is translated again, so a screen can never
// make a claim the English no longer makes. A translation whose placeholders or
// plural forms do not match the English shows English too. Missing = English.
//
// ── Message syntax (ICU lite) ───────────────────────────────────────────────
//   'Hello {name}'                                        a value
//   '{count, plural, one {# shift} other {# shifts}}'     plural: =0 =1 … zero one two few many other
//   '{who, select, me {You} other {{name}}}'              select on a word
//   'Tap <b>Post</b> to send'                             tags for t.rich (bold, links)
// `#` in a plural branch is the number, formatted for the language. Polish needs
// one/few/many/other, Romanian one/few/other, English, Portuguese and Spanish
// one/other (pluralCategory below, CLDR rules).

import { moneyText, MINUS, decimalMark } from '../moneyFormat.js'

export const DEFAULT_LANG = 'en'

// The picker's rows: the language's own name first, its English name under it.
export const LANGS = [
  { code: 'en', label: 'English', english: 'English', locale: 'en-GB' },
  { code: 'pl', label: 'Polski', english: 'Polish', locale: 'pl-PL' },
  { code: 'ro', label: 'Română', english: 'Romanian', locale: 'ro-RO' },
  { code: 'pt', label: 'Português', english: 'Portuguese', locale: 'pt-PT' },
  { code: 'es', label: 'Español', english: 'Spanish', locale: 'es-ES' },
]
export const LANG_CODES = LANGS.map((l) => l.code)
export const OTHER_LANGS = LANG_CODES.filter((c) => c !== DEFAULT_LANG)
export const langInfo = (code) => LANGS.find((l) => l.code === code) || LANGS[0]
export const localeOf = (code) => langInfo(code).locale

// FNV-1a, 32 bit, hex (the same fingerprint as shared/help/i18n). Not security.
export function hashText(s) {
  let h = 0x811c9dc5
  const str = String(s)
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}

// ── Plurals ─────────────────────────────────────────────────────────────────
// CLDR cardinal rules for our five languages. i = integer part, v = number of
// visible fraction digits.
export const PLURAL_FORMS = {
  en: ['one', 'other'],
  pl: ['one', 'few', 'many', 'other'],
  ro: ['one', 'few', 'other'],
  pt: ['one', 'other'],
  es: ['one', 'other'],
}
export function pluralCategory(lang, n) {
  const num = Math.abs(Number(n))
  if (!Number.isFinite(num)) return 'other'
  const str = String(num)
  const v = str.includes('.') ? str.split('.')[1].length : 0
  const i = Math.floor(num)
  switch (lang) {
    case 'pl': {
      if (i === 1 && v === 0) return 'one'
      if (v !== 0) return 'other'
      const m10 = i % 10
      const m100 = i % 100
      if (m10 >= 2 && m10 <= 4 && !(m100 >= 12 && m100 <= 14)) return 'few'
      return 'many'
    }
    case 'ro': {
      if (i === 1 && v === 0) return 'one'
      const m100 = i % 100
      if (v !== 0 || num === 0 || (m100 >= 2 && m100 <= 19) || (num !== 1 && m100 === 1)) return 'few'
      return 'other'
    }
    case 'pt':
      return i === 1 && v === 0 ? 'one' : 'other'
    case 'es':
      return num === 1 ? 'one' : 'other'
    default:
      return i === 1 && v === 0 ? 'one' : 'other'
  }
}

// ── The message parser ──────────────────────────────────────────────────────
// A message → nodes: strings, { arg }, { arg, kind: 'plural'|'select', branches }.
// Throws on a malformed message (the check script reports it; at run time a
// broken translation falls back to English, a broken English shows as written).
const PARSED = new Map()
export function parseMessage(src) {
  const s = String(src)
  if (PARSED.has(s)) return PARSED.get(s)
  let pos = 0
  const fail = (why) => { throw new Error(`${why} at ${pos} in "${s}"`) }
  const parseNodes = (inBranch, hashIsNumber = false) => {
    const nodes = []
    let text = ''
    while (pos < s.length) {
      const c = s[pos]
      if (c === '}') {
        if (!inBranch) fail('unexpected }')
        break
      }
      if (c === '#' && hashIsNumber) { if (text) nodes.push(text); text = ''; nodes.push({ hash: true }); pos++; continue }
      if (c !== '{') { text += c; pos++; continue }
      if (text) nodes.push(text)
      text = ''
      pos++
      const m = /^\s*([A-Za-z0-9_]+)\s*(?:,\s*(plural|select)\s*,)?/.exec(s.slice(pos))
      if (!m) fail('bad placeholder')
      pos += m[0].length
      const arg = m[1]
      if (!m[2]) {
        while (s[pos] === ' ') pos++
        if (s[pos] !== '}') fail('unclosed placeholder')
        pos++
        nodes.push({ arg })
        continue
      }
      const branches = {}
      for (;;) {
        while (/\s/.test(s[pos] || '')) pos++
        if (s[pos] === '}') { pos++; break }
        const k = /^(=\d+|[A-Za-z]+)\s*\{/.exec(s.slice(pos))
        if (!k) fail('bad branch')
        pos += k[0].length
        branches[k[1]] = parseNodes(true, m[2] === 'plural' || hashIsNumber)
        if (s[pos] !== '}') fail('unclosed branch')
        pos++
      }
      if (!branches.other) fail(`${m[2]} without other`)
      nodes.push({ arg, kind: m[2], branches })
    }
    if (text) nodes.push(text)
    return nodes
  }
  const nodes = parseNodes(false)
  if (pos < s.length) fail('trailing }')
  PARSED.set(s, nodes)
  return nodes
}

// What a message needs from its caller and its translators: every argument
// name, and for each plural/select argument the branches it has.
export function messageShape(src) {
  const args = new Set()
  const branches = {}
  const walk = (nodes) => {
    for (const n of nodes) {
      if (typeof n === 'string' || n.hash) continue
      args.add(n.arg)
      if (n.kind) {
        branches[n.arg] = { kind: n.kind, keys: Object.keys(n.branches) }
        for (const b of Object.values(n.branches)) walk(b)
      }
    }
  }
  walk(parseMessage(src))
  const tags = [...String(src).matchAll(/<\/?(\w+)\s*\/?>/g)].map((m) => m[0].replace(/\s/g, '')).sort()
  return { args: [...args].sort(), branches, tags }
}

// Does a translation fit its English? Same arguments, same tags, same select
// words, and every plural form the language needs. '' when it does, else why.
export function shapeProblem(en, tr, lang) {
  let a
  let b
  try { a = messageShape(en) } catch (e) { return `English: ${e.message}` }
  try { b = messageShape(tr) } catch (e) { return e.message }
  if (a.args.join() !== b.args.join()) return `placeholders ${b.args.join(',') || 'none'} should be ${a.args.join(',') || 'none'}`
  if (a.tags.join() !== b.tags.join()) return `tags ${b.tags.join('') || 'none'} should be ${a.tags.join('') || 'none'}`
  for (const [arg, eb] of Object.entries(a.branches)) {
    const tb = b.branches[arg]
    if (!tb || tb.kind !== eb.kind) return `{${arg}} should be a ${eb.kind}`
    if (eb.kind === 'select') {
      const miss = eb.keys.filter((k) => !tb.keys.includes(k))
      if (miss.length) return `{${arg}} select is missing ${miss.join(', ')}`
    } else {
      const need = (PLURAL_FORMS[lang] || ['other']).filter((k) => !tb.keys.includes(k))
      // `=N` exact branches in the English must survive too (=0 is often a different sentence).
      const exact = eb.keys.filter((k) => k.startsWith('=') && !tb.keys.includes(k))
      if (need.length || exact.length) return `{${arg}} plural needs ${[...need, ...exact].join(', ')}`
    }
  }
  return ''
}

// ── Formatting numbers, money, dates (Intl, with a plain fallback) ──────────
const NF = new Map()
function numberFormat(lang, opts) {
  const k = `${lang}|${JSON.stringify(opts || {})}`
  if (!NF.has(k)) {
    let f = null
    try { f = new Intl.NumberFormat(localeOf(lang), opts) } catch { f = null }
    NF.set(k, f)
  }
  return NF.get(k)
}
export function formatNumber(n, lang = DEFAULT_LANG, opts = {}) {
  const num = Number(n)
  if (!Number.isFinite(num)) return String(n ?? '')
  const f = numberFormat(lang, opts)
  if (f) { try { return f.format(num) } catch { /* fall through */ } }
  const d = opts.maximumFractionDigits ?? (Number.isInteger(num) ? 0 : 2)
  return num.toFixed(Math.min(d, 20)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

// Money is always pounds sterling, written ONE way (shared/moneyFormat.js):
// the £ sign first in every language, the language's own thousands and decimal
// marks: "£1,240.00", pl "£1 240,00", es "£1.240,00", a refund "−£12.50".
// `pence: true` when the amount is in pence. `whole: true` drops .00 on whole
// pounds. `signed: true` writes "+" before money in.
export function formatMoney(amount, lang = DEFAULT_LANG, opts = {}) {
  return moneyText(amount, lang, opts)
}

// A chart axis or a small stat: "£9.5K", "£850" in English; the language's own
// short number after the £ elsewhere ("£9,5 tys.", "£9,5 mil"). Pounds.
export function formatMoneyCompact(pounds, lang = DEFAULT_LANG, { maximumFractionDigits = 1 } = {}) {
  const v = Number(pounds)
  if (!Number.isFinite(v)) return ''
  const sign = v < 0 ? MINUS : ''
  const a = Math.abs(v)
  if (lang === DEFAULT_LANG || !LANG_CODES.includes(lang)) {
    if (a >= 1000) return `${sign}£${(a / 1000).toFixed(maximumFractionDigits).replace(/\.0+$/, '')}K`
    return `${sign}£${Math.round(a)}`
  }
  if (a < 1000) return `${sign}£${Math.round(a)}`
  const f = numberFormat(lang, { notation: 'compact', maximumFractionDigits })
  let out = null
  if (f) { try { out = f.format(a) } catch { out = null } }
  // An engine without compact notation: the English shape with the language's decimal mark.
  if (!out) out = `${(a / 1000).toFixed(maximumFractionDigits).replace(/\.0+$/, '').replace('.', decimalMark(lang))}K`
  return `${sign}£${out}`
}

// Named date and time styles, so a screen asks for a shape, not a pattern. In
// English each matches what the apps already write (en-GB):
//   weekdayDayMonth  "Tue 6 Oct"          dayMonth   "6 Oct"
//   dayMonthYear     "6 Oct 2026"         weekdayLong "Tuesday"
//   monthYear        "October 2026"       fullDate   "Tuesday 6 October 2026"
//   time             "09:00" (24 hour, as the apps write it)
export const DATE_STYLES = {
  weekdayDayMonth: { weekday: 'short', day: 'numeric', month: 'short' },
  dayMonth: { day: 'numeric', month: 'short' },
  dayMonthYear: { day: 'numeric', month: 'short', year: 'numeric' },
  weekdayLong: { weekday: 'long' },
  weekdayShort: { weekday: 'short' },
  monthYear: { month: 'long', year: 'numeric' },
  monthLong: { month: 'long' },
  fullDate: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  time: { hour: '2-digit', minute: '2-digit', hour12: false },
}
const DF = new Map()
export function formatDate(date, lang = DEFAULT_LANG, style = 'weekdayDayMonth', { timeZone = 'Europe/London' } = {}) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  const opts = { ...(typeof style === 'string' ? DATE_STYLES[style] || DATE_STYLES.weekdayDayMonth : style), timeZone }
  const k = `${lang}|${JSON.stringify(opts)}`
  if (!DF.has(k)) {
    let f = null
    try { f = new Intl.DateTimeFormat(localeOf(lang), opts) } catch { f = null }
    DF.set(k, f)
  }
  const f = DF.get(k)
  // en-GB puts a comma after the weekday ("Tue, 6 Oct") on some engines; the apps never did.
  if (f) { try { const out = f.format(d); return lang === 'en' ? out.replace(/^(\w{3,9}),\s/, '$1 ') : out } catch { /* fall through */ } }
  return d.toISOString().slice(0, 10)
}
export const formatTime = (date, lang = DEFAULT_LANG, opts) => formatDate(date, lang, 'time', opts)

// ── Filling a message ───────────────────────────────────────────────────────
function render(nodes, vars, lang, hashValue) {
  let out = ''
  for (const n of nodes) {
    if (typeof n === 'string') { out += n; continue }
    if (n.hash) { out += hashValue == null ? '#' : formatNumber(hashValue, lang); continue }
    const v = vars ? vars[n.arg] : undefined
    if (!n.kind) { out += v === undefined || v === null ? `{${n.arg}}` : String(v); continue }
    if (n.kind === 'select') {
      out += render(n.branches[String(v)] || n.branches.other, vars, lang, hashValue)
      continue
    }
    const num = Number(v)
    const exact = n.branches[`=${num}`]
    const branch = exact || n.branches[pluralCategory(lang, num)] || n.branches.other
    out += render(branch, vars, lang, num)
  }
  return out
}
export function formatMessage(src, vars, lang = DEFAULT_LANG) {
  if (typeof src !== 'string') return ''
  if (!/[{#]/.test(src)) return src
  try { return render(parseMessage(src), vars, lang, null) } catch { return src }
}

// "<b>Paid</b> the day you work" → [{ tag: 'b', text: 'Paid' }, ' the day you work'].
export function richParts(s) {
  const out = []
  const re = /<(\w+)\s*\/>|<(\w+)>([\s\S]*?)<\/\2>/g
  let last = 0
  let m
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index))
    out.push(m[1] ? { tag: m[1], text: '' } : { tag: m[2], text: m[3] })
    last = re.lastIndex
  }
  if (last < s.length) out.push(s.slice(last))
  return out
}

// ── Message packs ───────────────────────────────────────────────────────────
export function flatten(obj, prefix = '', out = {}) {
  if (obj == null) return out
  if (typeof obj === 'string') { out[prefix] = obj; return out }
  if (typeof obj !== 'object') return out
  for (const [k, v] of Object.entries(obj)) flatten(v, prefix ? `${prefix}.${k}` : k, out)
  return out
}
export function unflatten(flat) {
  const root = {}
  for (const [key, val] of Object.entries(flat)) {
    const parts = key.split('.')
    let node = root
    parts.forEach((p, i) => {
      if (i === parts.length - 1) node[p] = val
      else node = node[p] ??= {}
    })
  }
  const fix = (n) => {
    if (!n || typeof n !== 'object') return n
    const keys = Object.keys(n)
    for (const k of keys) n[k] = fix(n[k])
    return keys.length && keys.every((k, i) => String(i) === k) ? keys.map((k) => n[k]) : n
  }
  return fix(root)
}

// The stamp a translation carries: which English it was made from, and which
// translation text was stamped (so `stamp` never re-blesses an untouched stale line).
export const stampOf = (en, tr) => `${hashText(en)}.${hashText(tr)}`
export const stampIsCurrent = (stamp, en) => typeof stamp === 'string' && stamp.split('.')[0] === hashText(en)

// A namespace's words: defineMessages('practice/app/theme', { en, pl, ro, pt, es }).
// `en` is the nested English; each other is { messages, stamps } from its .json.
export function defineMessages(ns, packs = {}) {
  const en = flatten(packs.en || {})
  return { ns, en, packs, resolved: {} }
}

// The flat words a namespace shows in `lang`: each English key, translated where
// the translation is current and fits, English otherwise.
export function resolveMessages(M, lang) {
  if (!M || !M.en) return {}
  if (!lang || lang === DEFAULT_LANG || !M.packs[lang]) return M.en
  if (M.resolved[lang]) return M.resolved[lang]
  const pack = M.packs[lang] || {}
  const tr = flatten(pack.messages || {})
  const stamps = pack.stamps || {}
  const out = {}
  for (const [k, en] of Object.entries(M.en)) {
    const t = tr[k]
    out[k] = typeof t === 'string' && t && stampIsCurrent(stamps[k], en) && !shapeProblem(en, t, lang) ? t : en
  }
  M.resolved[lang] = out
  return out
}

const warned = new Set()
export function makeT(M, lang = DEFAULT_LANG) {
  const msgs = resolveMessages(M, lang)
  const lookup = (key) => {
    const s = msgs[key]
    if (s === undefined) {
      const id = `${M?.ns}:${key}`
      if (!warned.has(id)) { warned.add(id); try { if (typeof __DEV__ !== 'undefined' && __DEV__) console.warn(`[i18n] missing key ${id}`) } catch { /* node */ } }
      return key
    }
    return s
  }
  const t = (key, vars) => formatMessage(lookup(key), vars, lang)
  t.lang = lang
  t.locale = localeOf(lang)
  t.has = (key) => key in msgs
  // t.rich('lead', { b: (text, i) => <Text key={i} style={bold}>{text}</Text> }, vars)
  // → an array of strings and whatever the renderers return (put it inside a <Text>).
  t.rich = (key, comps = {}, vars) => richParts(formatMessage(lookup(key), vars, lang))
    .map((p, i) => (typeof p === 'string' ? p : comps[p.tag] ? comps[p.tag](p.text, i) : p.text))
  // Everything under `key.` in its shape: arrays for 0..n keys (lists of copy).
  t.list = (key) => {
    const pre = `${key}.`
    const flat = {}
    for (const [k, v] of Object.entries(msgs)) if (k.startsWith(pre)) flat[k.slice(pre.length)] = v
    return unflatten(flat)
  }
  t.number = (n, opts) => formatNumber(n, lang, opts)
  t.money = (amount, opts) => formatMoney(amount, lang, opts)
  t.moneyCompact = (pounds, opts) => formatMoneyCompact(pounds, lang, opts)
  t.date = (d, style, opts) => formatDate(d, lang, style, opts)
  t.time = (d, opts) => formatTime(d, lang, opts)
  return t
}

// ── The language in use ─────────────────────────────────────────────────────
// One per running app. The app binding (lib/i18n.js) calls initLanguage once at
// import with the languages this build may show (its i18n/manifest.json
// `released`, every language in a dev build), the phone's locales and the
// device storage. The choice ('auto' or a code) is kept on the phone and
// survives a sign out, like the theme.
const state = { lang: DEFAULT_LANG, choice: 'auto', offered: [DEFAULT_LANG], device: [], storage: null, key: null }
const listeners = new Set()
const emit = () => { for (const fn of listeners) { try { fn() } catch { /* one bad listener */ } } }

export const getLanguage = () => state.lang
export const getLanguageChoice = () => state.choice
// The phone's own locales as the app was given them ('pl-PL'), for records that
// note the device language beside the one in use (shared/consentText.js).
export const getDeviceLocales = () => [...state.device]
export const offeredLanguages = () => LANGS.filter((l) => state.offered.includes(l.code))
export function subscribeLanguage(fn) { listeners.add(fn); return () => listeners.delete(fn) }

// 'pl-PL', 'pl_PL', 'pl' → 'pl'; the first of the phone's languages we offer, else English.
export function deviceLanguage(locales = state.device, offered = state.offered) {
  for (const raw of locales || []) {
    const code = String(raw || '').toLowerCase().split(/[-_]/)[0]
    // A phone set to Brazilian Portuguese (pt-BR) reads European Portuguese.
    if (offered.includes(code)) return code
  }
  return DEFAULT_LANG
}

export function pickLanguage(choice, locales, offered) {
  if (choice && choice !== 'auto' && offered.includes(choice)) return choice
  return deviceLanguage(locales, offered)
}

function apply() {
  const next = pickLanguage(state.choice, state.device, state.offered)
  if (next !== state.lang) { state.lang = next; emit() }
}

export function initLanguage({ released = [], dev = false, deviceLocales = [], storage = null, key = 'tempdash.language.v1' } = {}) {
  state.offered = [DEFAULT_LANG, ...(dev ? OTHER_LANGS : OTHER_LANGS.filter((c) => released.includes(c)))]
  state.device = deviceLocales.filter(Boolean)
  state.storage = storage
  state.key = key
  apply()
  if (storage) {
    Promise.resolve()
      .then(() => storage.getItem(key))
      .then((saved) => { if (saved && (saved === 'auto' || LANG_CODES.includes(saved))) { state.choice = saved; apply(); emit() } })
      .catch(() => { /* the phone's language it is */ })
  }
}

export function setLanguageChoice(choice) {
  state.choice = choice === 'auto' || LANG_CODES.includes(choice) ? choice : 'auto'
  if (state.storage) Promise.resolve().then(() => state.storage.setItem(state.key, state.choice)).catch(() => {})
  apply()
  emit()
}

// Outside a component (a lib helper, a shared/ function): the words in the
// language in use right now. Inside a component use the app's useT, which
// re-renders when the language changes.
export const tNow = (M) => makeT(M, state.lang)
